import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, SlidersHorizontal } from 'lucide-react';
import { Button, Composer, InstrumentTag } from '../ds';
import { inSentence } from '../ds/Composer';
import { Thread } from './Thread';
import { MeteringDrawer } from './MeteringDrawer';
import { EditClipDialog } from './ClipDialogs';
import { ErrorNote } from './ErrorNote';
import { useClip } from '../../hooks/useClip';
import { useClipChat } from '../../hooks/useClipChat';
import { useAnalysis } from '../../hooks/analysisStore';
import { useAudioFileInput } from '../../hooks/useAudioFileInput';
import { useStudioReady } from '../studio/studioEntrance';

// Match the backend's limits on text sent to the AI model
const MAX_CHAT_CHARS = 2000;

// What the composer says while a version is analyzed
const lockedPlaceholder = (name, version) => `You can ask about the ${inSentence(name)} once V${version} is scored`;

/**
 * A clip that's still being created: its first upload and the analyzing card, with the composer
 * locked. The workspace swaps in the real clip's ClipPanel once the analysis is done.
 */
export const DraftClipPanel = ({ clip }) => {
  const analysis = useAnalysis(clip.id);
  const info = analysis?.info;

  return (
    <section aria-label={`${clip.name} chat`} className="flex-1 min-w-0 min-h-0 flex flex-col bg-base">
      <header className="md:hidden flex items-center gap-space-2 px-space-4 py-space-3 border-b border-raised">
        <h1 className="m-0 font-display text-title-3 font-extrabold text-ink break-words">{clip.name}</h1>
        <InstrumentTag category={clip.category} type={clip.instrument_type} />
      </header>
      <div className="flex-1 min-h-0 md:overflow-y-auto md:overscroll-contain">
        <div className="w-full max-w-chat-max mx-auto px-space-4 sm:px-space-6 py-space-6">
          <Thread versions={[]} messages={[]} pending={info} />
        </div>
      </div>
      <div className="sticky bottom-0 px-space-4 sm:px-space-6 pt-space-4 pb-space-5 bg-base border-t border-raised">
        <Composer clipName={clip.name} nextVersion={2} onFileChange={() => {}} onSubmit={() => false} lockedPlaceholder={lockedPlaceholder(clip.name, 1)} />
      </div>
    </section>
  );
};

/**
 * One clip's chat: the thread and the composer, with a compact clip header of its own on phones
 * (from tablet up the clip's name, Edit and Upload live in the studio bar). A file dropped anywhere
 * here, or picked with Upload, becomes the clip's next version once sent.
 *
 * The studio bar drives it through `editing` / `onEdit` (the Edit clip dialog), `uploadInputRef`
 * (the hidden file input its Upload button clicks) and `onBusyChange` (while a version or reply is
 * on its way). Without them it keeps that state itself.
 */
export const ClipPanel = ({
  clipId, isVerified, onNeedsVerification, onClipChange, onClipDeleted, onCredits,
  editing: editingProp, onEdit, uploadInputRef, onBusyChange,
}) => {
  const [error, setError] = useState(null);
  const [meteringVersion, setMeteringVersion] = useState(null);
  const [ownEditing, setOwnEditing] = useState(false);
  const editing = editingProp ?? ownEditing;
  const setEditing = onEdit ?? setOwnEditing;
  const {
    clip, versions, messages, loading, error: loadError, pending, failure, clearFailure, nextVersion, addVersion, update, remove, appendMessages,
  } = useClip(clipId, { onChange: onClipChange, onCredits });
  const chat = useClipChat(clipId, { appendMessages, onCredits });
  const files = useAudioFileInput({ canUpload: true, isBusy: Boolean(pending), setError });
  const ownUploadRef = useRef(null);
  const uploadRef = uploadInputRef || ownUploadRef;
  const busy = Boolean(pending) || chat.isSending;
  useStudioReady(!loading);

  // The studio bar's Upload waits while something is on its way, and frees up when this clip closes
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  const onFileChange = useCallback((file) => {
    if (file) files.handleFileSelect(file);
    else files.setFile(null);
  }, [files]);

  const fail = (err) => {
    if (err.status === 403) onNeedsVerification();
    else setError(err.message || 'Something went wrong. Please try again.');
    throw err;
  };

  // Why an analysis failed shows here, even when it failed while another clip was open
  const onNeedsVerificationRef = useRef(onNeedsVerification);
  onNeedsVerificationRef.current = onNeedsVerification;
  useEffect(() => {
    if (!failure) return;
    if (failure.status === 403) onNeedsVerificationRef.current();
    else setError(failure.message || 'Something went wrong. Please try again.');
    clearFailure();
  }, [failure, clearFailure]);

  const submit = async ({ text, file }) => {
    setError(null);
    if (file) {
      if (!isVerified) {
        onNeedsVerification();
        return false;
      }
      files.setFile(null);
      try {
        await addVersion({ file, note: text });
      } catch (err) {
        files.setFile(file); // keep it attached so they can try again; the failure effect says why
        throw err;
      }
      return true;
    }
    try {
      await chat.send(text);
    } catch (err) {
      fail(err);
    }
    return true;
  };

  if (loading) {
    return <div role="status" className="flex-1 p-space-8 text-small text-muted font-text">Loading the clip…</div>;
  }
  if (loadError || !clip) {
    return <div className="flex-1 p-space-8"><ErrorNote>{loadError || 'We couldn\'t load this clip.'}</ErrorNote></div>;
  }

  return (
    <div className="flex-1 min-w-0 min-h-0 flex">
      <section
        aria-label={`${clip.name} chat`}
        className="relative flex-1 min-w-0 flex flex-col bg-base"
        onDragOver={files.onDragOver}
        onDrop={files.onDrop}
      >
        {files.isDragging && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center p-space-6 bg-base/90"
            onDragOver={files.onDragOver}
            onDragLeave={files.onDragLeave}
            onDrop={files.onDrop}
          >
            <div className="pointer-events-none px-space-8 py-space-10 rounded-radius-xl border-2 border-dashed border-amber text-center font-text">
              <p className="m-0 font-display text-title-3 text-ink">Drop to add V{nextVersion} of {clip.name}</p>
              <p className="m-0 mt-space-2 text-small text-muted">New instruments go in Add clip.</p>
            </div>
          </div>
        )}

        {/* Phones only: from tablet up the clip's name and actions are in the studio bar */}
        <header className="md:hidden flex items-center justify-between gap-space-2 pl-space-4 pr-space-2 py-space-2 border-b border-raised">
          <div className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1 min-w-0">
            <h1 className="m-0 font-display text-title-3 font-extrabold text-ink break-words">{clip.name}</h1>
            <InstrumentTag category={clip.category} type={clip.instrument_type} />
            {clip.latest_version && <span className="font-mono text-caption text-muted">V{clip.latest_version}</span>}
          </div>
          <div className="flex-none flex items-center gap-space-1">
            <Button variant="icon" icon={SlidersHorizontal} aria-label="Edit clip" onClick={() => setEditing(true)} />
            <Button
              variant="icon"
              icon={Upload}
              aria-label={`Upload V${nextVersion}`}
              onClick={() => uploadRef.current?.click()}
              disabled={busy}
              className="border border-control text-ink"
            />
          </div>
        </header>
        <input
          ref={uploadRef}
          type="file"
          accept="audio/*"
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
          data-testid="upload-next-version"
          onChange={(event) => {
            if (event.target.files?.[0]) files.handleFileSelect(event.target.files[0]);
            event.target.value = '';
          }}
        />

        <div className="flex-1 min-h-0 md:overflow-y-auto md:overscroll-contain">
          <div className="w-full max-w-chat-max mx-auto px-space-4 sm:px-space-6 py-space-6">
            <Thread
              versions={versions}
              messages={messages}
              pending={pending}
              pendingMessage={chat.pendingMessage}
              onShowMetering={setMeteringVersion}
            />
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-col gap-space-3 px-space-4 sm:px-space-6 pt-space-4 pb-space-5 bg-base border-t border-raised">
          {error && (
            <div className="w-full max-w-chat-max mx-auto">
              <ErrorNote onDismiss={() => setError(null)}>{error}</ErrorNote>
            </div>
          )}
          <Composer
            clipName={clip.name}
            nextVersion={nextVersion}
            file={files.file}
            onFileChange={onFileChange}
            onSubmit={submit}
            busy={busy}
            lockedPlaceholder={pending ? lockedPlaceholder(clip.name, pending.version) : undefined}
            maxLength={MAX_CHAT_CHARS}
          />
        </div>
      </section>

      {meteringVersion && <MeteringDrawer version={meteringVersion} onClose={() => setMeteringVersion(null)} />}

      <EditClipDialog
        open={editing}
        clip={clip}
        onClose={() => setEditing(false)}
        onSave={async (changes) => {
          await update(changes);
          setEditing(false);
        }}
        onDelete={async () => {
          await remove();
          setEditing(false);
          onClipDeleted(clip.id);
        }}
      />
    </div>
  );
};
